import React from "react";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "warning"
    | "ghost"
    | "outline";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  children,
  disabled,
  className = "",
  ...props
}: ButtonProps) {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-bold rounded-xl
    transition-all duration-200
    focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
    active:scale-[0.98]
    select-none
  `
    .trim()
    .replace(/\s+/g, " ");

  const variantStyles = {
    primary: `
      bg-gradient-to-r from-primary-600 to-primary-700
      text-white shadow-lg shadow-primary-600/25
      hover:from-primary-700 hover:to-primary-800 hover:shadow-xl hover:shadow-primary-600/30
      focus-visible:ring-primary-500
    `,
    secondary: `
      bg-white text-primary-700 border border-primary-200
      shadow-sm shadow-slate-200/70
      hover:bg-primary-50 hover:border-primary-300
      focus-visible:ring-primary-500
    `,
    success: `
      bg-gradient-to-r from-accent-600 to-accent-700
      text-white shadow-lg shadow-accent-500/25
      hover:from-accent-700 hover:to-accent-800 hover:shadow-xl hover:shadow-accent-500/30
      focus-visible:ring-accent-500
    `,
    danger: `
      bg-gradient-to-r from-rose-600 to-rose-700
      text-white shadow-lg shadow-rose-500/25
      hover:from-rose-700 hover:to-rose-800 hover:shadow-xl hover:shadow-rose-500/30
      focus-visible:ring-rose-500
    `,
    warning: `
      bg-gradient-to-r from-amber-500 to-amber-600
      text-white shadow-lg shadow-amber-500/25
      hover:from-amber-600 hover:to-amber-700 hover:shadow-xl hover:shadow-amber-500/30
      focus-visible:ring-amber-500
    `,
    ghost: `
      bg-transparent text-slate-700
      hover:bg-slate-100 hover:text-slate-950
      focus-visible:ring-slate-300
    `,
    outline: `
      bg-white text-slate-700 border border-slate-200
      shadow-sm shadow-slate-200/70
      hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700
      focus-visible:ring-primary-500
    `,
  };

  const sizeStyles = {
    xs: "h-8 px-3 text-xs",
    sm: "h-10 px-4 text-sm",
    md: "h-11 px-5 text-sm",
    lg: "h-12 px-6 text-base",
    xl: "h-14 px-8 text-lg",
  };

  const widthStyle = fullWidth ? "w-full" : "";

  return (
    <button
      className={`
        ${baseStyles}
        ${variantStyles[variant].trim().replace(/\s+/g, " ")}
        ${sizeStyles[size]}
        ${widthStyle}
        ${className}
      `
        .trim()
        .replace(/\s+/g, " ")}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Carregando...</span>
        </>
      ) : (
        <>
          {leftIcon && <span className="inline-flex">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="inline-flex">{rightIcon}</span>}
        </>
      )}
    </button>
  );
}
